// ─────────────────────────────────────────────────────────────────
// Sehat AI — useLocation Custom Hook
// Gets user's GPS coordinates and city name.
//
// When permission is denied:
//   • city   = null  (no fake default shown)
//   • denied = true  (UI can show a retry button)
//   • ready  = true  (app can still run, just without location)
//
// retry() re-requests permission (increments retryCount → re-runs effect)
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react';
import { reverseGeocode } from '../utils/placesApi';

export function useLocation() {
  const [retryCount, setRetryCount] = useState(0);
  const [location, setLocation] = useState({
    lat:    null,   // null until GPS gives real coords
    lng:    null,
    city:   null,
    ready:  false,
    denied: false,
    error:  null,
  });

  useEffect(() => {
    // Reset state on each retry
    setLocation(prev => ({ ...prev, ready: false, denied: false, error: null, city: null }));

    if (!navigator.geolocation) {
      setLocation(prev => ({ ...prev, ready: true, denied: true, error: 'Geolocation not supported' }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setLocation(prev => ({ ...prev, lat, lng }));

        try {
          const city = await reverseGeocode(lat, lng);
          setLocation(prev => ({ ...prev, city: city || null, ready: true, denied: false }));
        } catch {
          // Reverse geocoding failed — coords are valid, city unknown
          setLocation(prev => ({ ...prev, city: null, ready: true, denied: false }));
        }
      },
      (err) => {
        // Permission denied or timed out — do NOT fall back to a fake default city
        setLocation(prev => ({
          ...prev,
          city:   null,
          ready:  true,
          denied: true,
          error:  err.message || 'Location access denied',
        }));
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  }, [retryCount]); // re-runs when user clicks "Allow Location"

  const retry = useCallback(() => setRetryCount(c => c + 1), []);

  return { ...location, retry };
}
